import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useState } from "react";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { match } from "ts-pattern";
import { useTranslation } from "@i18n/hooks";
import { type rewardEditor_DeleteRewardMutation } from "@relay/rewardEditor_DeleteRewardMutation.graphql";
import { type rewardEditor_Query } from "@relay/rewardEditor_Query.graphql";
import { type rewardForm_RewardFragment$key } from "@relay/rewardForm_RewardFragment.graphql";
import { QUERY, REMOVE_REWARD_MUTATION, TREE_NODE_FRAGMENT } from "./reward-editor.graphql";
import { buttonContainerClass, OrderListWithoutControls } from "./reward-editor.styles";
import { type RewardEditorProps } from "./reward-editor.types";
import { WarningUnsavedChangesDialog } from "../../../../components/WarningUnsavedChangesDialog";
import { selectArrayOfEditedForms } from "../../../../store/slices/CoreSlice";
import { useTypedSelector } from "../../../../store/store.redux";
import { makeMutableCopy } from "../../../../util/mutability";
import { CreateExternalLicenseRewardButton } from "../create-external-license-reward-button";
import { CreateIHKCertificateRewardBundleButton } from "../create-ihk-certificate-reward-bundle-button";
import { CreateIHKCertificateRewardButton } from "../create-ihk-certificate-reward-button/create-ihk-certificate-reward-button.component";
import { RewardForm } from "../reward-form";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const RewardEditor = ({ treeNodeFragmentRef }: RewardEditorProps) => {
	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const node = useFragment(TREE_NODE_FRAGMENT, treeNodeFragmentRef);

	const data = useLazyLoadQuery<rewardEditor_Query>(QUERY, {});

	const [removeReward, isRemovingReward] =
		useMutation<rewardEditor_DeleteRewardMutation>(REMOVE_REWARD_MUTATION);

	const [currentlyEditingReward, setCurrentlyEditingReward] = useState<
		rewardForm_RewardFragment$key | undefined
	>();
	const [showDialog, setShowDialog] = useState(false);

	const arrayOfEditedForm = useTypedSelector(selectArrayOfEditedForms);

	const handleOnBack = () => {
		if (arrayOfEditedForm.length > 0) {
			setShowDialog(true);
		} else {
			setCurrentlyEditingReward(undefined);
		}
	};

	const createOnDoubleClickHandler = (item: any) => () => {
		setCurrentlyEditingReward(item);
	};
	const createDeleteOnClickHanlder = (item: any) => () => {
		removeReward({
			variables: {
				input: {
					contentNodeId: node.id,
					rewardId: item.id,
				},
			},
			updater: setDraft,
		});
	};
	const createEditOnClickHandler = (item: any) => () => {
		setCurrentlyEditingReward(item);
	};

	const isInternalReward = (item: any) => {
		return item.kind === "GamificationPoints" || item.kind === "CrmTreeHeadUpdater";
	};

	const rewards = makeMutableCopy(node.typeDefinition.rewards ?? []);
	const hasIHKCertificateReward = rewards.some((reward) => reward.kind === "IHKCertificate");
	const hasIHKCertificateBundleReward = rewards.some(
		(reward) => reward.kind === "IHKCertificateBundle",
	);

	return (
		<Card className="mb-2">
			<h2>{t("educationalOffer.rewards.title")}</h2>
			{currentlyEditingReward ? (
				<>
					{showDialog && (
						<WarningUnsavedChangesDialog
							setShowDialog={setShowDialog}
							callback={setCurrentlyEditingReward}
							value={undefined}
						/>
					)}
					<RewardForm
						treeNodeFragmentRef={node}
						onBack={handleOnBack}
						rewardConfigurationFragmentRef={currentlyEditingReward}
					/>
				</>
			) : (
				<div>
					<div className={buttonContainerClass}>
						<CreateExternalLicenseRewardButton
							treeNodeFragmentRef={node}
							externalLicensePoolConnectionFragmentRef={
								data.Admin.Billing.GetExternalLicensePools
							}
						/>
						<CreateIHKCertificateRewardButton
							treeNodeFragmentRef={node}
							disabled={hasIHKCertificateReward || hasIHKCertificateBundleReward}
						/>
						<CreateIHKCertificateRewardBundleButton
							treeNodeFragmentRef={node}
							disabled={hasIHKCertificateReward || hasIHKCertificateBundleReward}
						/>
					</div>

					<OrderListWithoutControls
						dragdrop={false}
						itemTemplate={(item: (typeof rewards)[number]) => {
							return (
								<div
									className="flex align-items-center"
									onDoubleClick={createOnDoubleClickHandler(item)}
								>
									{match(item)
										.with({ kind: "ExternalLicense" }, (item) => (
											<span>
												{t("educationalOffer.rewards.externalLicense", {
													poolName: item.data?.pool?.data.name,
												})}
											</span>
										))
										.with({ kind: "IHKCertificate" }, () => (
											<span>
												{t("educationalOffer.rewards.ihkCertificate")}
											</span>
										))
										.with({ kind: "IHKCertificateBundle" }, () => (
											<span>
												{t("educationalOffer.rewards.ihkCertificateBundle")}
											</span>
										))
										.with({ kind: "GamificationPoints" }, () => (
											<span>
												{t("educationalOffer.rewards.gamificationPoints")}
											</span>
										))
										.with({ kind: "LearnStreak" }, () => (
											<span>{t("educationalOffer.rewards.learnStreak")}</span>
										))
										.with({ kind: "CrmTreeHeadUpdater" }, () => (
											<span>
												{t("educationalOffer.rewards.crmTreeHeadUpdater")}
											</span>
										))
										.otherwise(() => (
											<span>{t("educationalOffer.rewards.unknown")}</span>
										))}

									{!isInternalReward(item) && (
										<>
											<Button
												disabled={isRemovingReward}
												className="ml-auto"
												icon="pi pi-trash"
												onClick={createDeleteOnClickHanlder(item)}
											/>

											<Button
												disabled={isRemovingReward}
												className="ml-2"
												icon="pi pi-pencil"
												onClick={createEditOnClickHandler(item)}
											/>
										</>
									)}
								</div>
							);
						}}
						value={rewards}
					/>
				</div>
			)}
		</Card>
	);
};
